import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Check, Ban, RotateCcw, Bell, Loader2, Pencil, Plus, Trash2, CheckCircle2, X, FileDown, Palette, Building2 } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api, formatApiError, mediaUrl } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";
import { useBranding } from "../../context/BrandingContext";
import TenantBrandingDialog from "../../components/os/TenantBrandingDialog";
import FiscalCompaniesDialog from "../../components/os/FiscalCompaniesDialog";

const ease = [0.16, 1, 0.3, 1];
const ROLES = ["admin", "ceo", "ventas", "cuentas", "contador", "produccion", "disenador", "fabricante", "cliente", "usuario_rd"];
const ROLE_LABELS = {
  admin: "Administrador", ceo: "CEO", ventas: "Ventas", cuentas: "Cuentas", contador: "Contador",
  produccion: "Producción", disenador: "Diseñador", fabricante: "Fabricante", cliente: "Cliente",
  usuario_rd: "Usuario RD",
};
const STATUS_CLS = {
  pending: "bg-gold/15 text-gold", active: "bg-emerald-500/10 text-emerald-600", suspended: "bg-destructive/10 text-destructive",
};

export default function AdminUsers() {
  const { t } = useI18n();
  const { reload: reloadBranding } = useBranding();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [requests, setRequests] = useState([]);
  const [digesting, setDigesting] = useState(false);
  const [brandDialog, setBrandDialog] = useState({ open: false, tenant: null });
  const [fiscalDialog, setFiscalDialog] = useState({ open: false, tenant: null });
  const [compDialog, setCompDialog] = useState({ open: false, user: null, selected: [], roles: {}, default: null, loading: false, saving: false });

  const load = useCallback(() => { api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  const loadTenants = useCallback(() => { api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {}); }, []);
  const loadRequests = useCallback(() => { api.get("/tenants/requests").then((r) => setRequests(r.data)).catch(() => setRequests([])); }, []);
  useEffect(() => { load(); loadTenants(); loadRequests(); }, [load, loadTenants, loadRequests]);

  const approveReq = async (r) => {
    try { await api.post(`/tenants/requests/${r.id}/approve`); toast.success(`${r.company_name} aprobada y activada`); loadRequests(); loadTenants(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };
  const rejectReq = async (r) => {
    if (!window.confirm(`¿Rechazar la solicitud de "${r.company_name}"? No podrá ingresar.`)) return;
    try { await api.post(`/tenants/requests/${r.id}/reject`); toast.success("Solicitud rechazada"); loadRequests(); loadTenants(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };
  const deleteTenant = async (tn) => {
    if (!window.confirm(`ELIMINAR la empresa "${tn.name}" y TODOS sus datos (usuarios, registros, documentos). Esta acción es irreversible. ¿Continuar?`)) return;
    try { await api.delete(`/tenants/${tn.id}`); toast.success(`Empresa "${tn.name}" eliminada`); loadTenants(); loadRequests(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };
  const pendingReqs = requests.filter((r) => r.status === "pending");

  const update = async (id, body) => {
    try { await api.patch(`/auth/users/${id}`, body); load(); toast.success("OK"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`¿Eliminar definitivamente a ${u.name || u.email}? Esta acción no se puede deshacer.`)) return;
    try { await api.delete(`/auth/users/${u.id}`); load(); toast.success("Usuario eliminado"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  const openCompanies = async (u) => {
    setCompDialog({ open: true, user: u, selected: [], roles: {}, default: null, loading: true, saving: false });
    try {
      const r = await api.get(`/auth/users/${u.id}/companies`);
      const comps = r.data.companies || [];
      const roles = {}; comps.forEach((c) => { roles[c.company_id] = c.role || u.role; });
      setCompDialog((d) => ({ ...d, selected: comps.map((c) => c.company_id), roles, default: r.data.default_company_id, loading: false }));
    } catch (e) { setCompDialog((d) => ({ ...d, loading: false })); toast.error("No se pudieron cargar las empresas"); }
  };
  const toggleCompany = (cid) => setCompDialog((d) => {
    const has = d.selected.includes(cid);
    const selected = has ? d.selected.filter((c) => c !== cid) : [...d.selected, cid];
    const roles = { ...d.roles };
    if (!has && !roles[cid]) roles[cid] = d.user?.role || "cliente";
    let def = d.default;
    if (has && def === cid) def = selected[0] || null;
    if (!has && !def) def = cid;
    return { ...d, selected, roles, default: def };
  });
  const setCompanyRole = (cid, role) => setCompDialog((d) => ({ ...d, roles: { ...d.roles, [cid]: role } }));
  const saveCompanies = async () => {
    if (compDialog.selected.length === 0) return toast.error("Selecciona al menos una empresa");
    setCompDialog((d) => ({ ...d, saving: true }));
    try {
      await api.put(`/auth/users/${compDialog.user.id}/companies`, {
        companies: compDialog.selected.map((cid) => ({ company_id: cid, role: compDialog.roles[cid] || compDialog.user.role })),
        default_company_id: compDialog.default || compDialog.selected[0],
        notify_email: true,
      });
      toast.success(`${compDialog.user.name || "Usuario"}: ${compDialog.selected.length} empresa(s) asignada(s)`);
      setCompDialog({ open: false, user: null, selected: [], roles: {}, default: null, loading: false, saving: false });
      load();
    } catch (e) { setCompDialog((d) => ({ ...d, saving: false })); toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };
  const editEmail = async (u) => {
    const current = (u.email || "").split("@")[0];
    const username = window.prompt(`Nuevo usuario para ${u.name || u.email} (se le agregará @omnia.os):`, current);
    if (username === null) return;
    const clean = username.trim();
    if (!clean) return;
    update(u.id, { username: clean });
  };
  const editPersonalEmail = async (u) => {
    const val = window.prompt(`Correo electrónico personal de ${u.name || u.email} (para notificaciones):`, u.personal_email || "");
    if (val === null) return;
    const clean = val.trim().toLowerCase();
    if (!clean) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean)) { toast.error("Correo no válido"); return; }
    update(u.id, { personal_email: clean });
  };
  const runDigest = async () => {
    setDigesting(true);
    try { const { data } = await api.post("/automation/digest/run"); toast.success(`${t("os.admin.digestSent")} · ${data.overdue} / ${data.low_stock} / ${data.hot_leads}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setDigesting(false); }
  };

  const pending = users.filter((u) => u.status === "pending");
  const rest = users.filter((u) => u.status !== "pending");

  const Row = ({ u }) => (
    <div data-testid={`admin-user-${u.id}`} className="grid grid-cols-2 sm:grid-cols-[1.2fr_1.4fr_130px_110px_1fr] gap-3 px-5 py-4 items-center border-b border-navy/5 last:border-0">
      <div className="font-medium text-sm">{u.name || "—"}</div>
      <div className="text-sm text-navy/60 truncate flex flex-col gap-0.5">
        <div className="flex items-center gap-1.5">
          <span className="truncate">{u.email}</span>
          <button data-testid={`admin-edit-email-${u.id}`} onClick={() => editEmail(u)}
            className="text-navy/30 hover:text-violet transition-colors shrink-0" title="Editar usuario/correo">
            <Pencil className="w-3.5 h-3.5" />
          </button>
        </div>
        {u.personal_email
          ? <span className="text-[11px] text-navy/40 truncate flex items-center gap-1.5" title="Correo personal (notificaciones)">
              ✉ {u.personal_email}
              <button data-testid={`admin-edit-personal-${u.id}`} onClick={() => editPersonalEmail(u)} className="text-navy/30 hover:text-violet transition-colors shrink-0" title="Editar correo personal"><Pencil className="w-3 h-3" /></button>
            </span>
          : <button data-testid={`admin-add-personal-${u.id}`} onClick={() => editPersonalEmail(u)} className="text-[11px] text-violet/70 hover:text-violet transition-colors flex items-center gap-1 w-fit" title="Agregar correo personal">
              <Plus className="w-3 h-3" /> {t("os.admin.addPersonalEmail")}
            </button>}
      </div>
      <div className="flex flex-col gap-1">
        <select data-testid={`admin-role-${u.id}`} value={u.role} onChange={(e) => update(u.id, { role: e.target.value })}
          className="text-xs bg-white border border-navy/10 rounded-lg px-2 py-1.5 outline-none focus:border-violet">
          {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
        </select>
        <button data-testid={`admin-companies-${u.id}`} onClick={() => openCompanies(u)}
          className="text-[11px] bg-cream border border-navy/10 rounded-lg px-2 py-1.5 outline-none hover:border-violet flex items-center justify-between gap-1 text-left" title="Empresas (multiempresa)">
          <span className="truncate">{tenants.find((tn) => tn.id === u.tenant_id)?.name || "Empresa"}</span>
          <Building2 className="w-3 h-3 text-navy/40 shrink-0" />
        </button>
      </div>
      <span className={`text-[11px] px-2 py-1 rounded-full justify-self-start ${STATUS_CLS[u.status]}`}>{t(`os.admin.statusLabels.${u.status}`)}</span>
      <div className="flex items-center gap-2 justify-end">
        {u.status !== "active" && (
          <button data-testid={`admin-approve-${u.id}`} onClick={() => update(u.id, { status: "active" })}
            className="inline-flex items-center gap-1.5 text-xs rounded-full bg-emerald-500/10 text-emerald-600 px-3 py-1.5 hover:bg-emerald-500/20 transition-colors">
            <Check className="w-3.5 h-3.5" /> {u.status === "pending" ? t("os.admin.approve") : t("os.admin.activate")}
          </button>
        )}
        {u.status === "active" && u.role !== "admin" && (
          <button data-testid={`admin-suspend-${u.id}`} onClick={() => update(u.id, { status: "suspended" })}
            className="inline-flex items-center gap-1.5 text-xs rounded-full border border-navy/10 text-navy/50 px-3 py-1.5 hover:text-destructive hover:border-destructive/40 transition-colors">
            <Ban className="w-3.5 h-3.5" /> {t("os.admin.suspend")}
          </button>
        )}
        {u.status !== "active" && u.role !== "admin" && (
          <button data-testid={`admin-delete-${u.id}`} onClick={() => removeUser(u)}
            title="Eliminar definitivamente"
            className="inline-flex items-center gap-1.5 text-xs rounded-full border border-destructive/20 text-destructive/70 px-3 py-1.5 hover:bg-destructive/10 hover:text-destructive transition-colors">
            <Trash2 className="w-3.5 h-3.5" /> Eliminar
          </button>
        )}
      </div>
    </div>
  );

  return (
    <OSLayout title={t("os.nav.admin")}>
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
            <div className="overline text-navy/40 mb-2">OMNIA · Access</div>
            <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.admin.title")}</h2>
            <p className="mt-2 text-navy/50">{t("os.admin.subtitle")}</p>
          </motion.div>
          <button data-testid="admin-run-digest" onClick={runDigest} disabled={digesting}
            className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-4 py-2.5 text-sm hover:border-violet hover:text-violet transition-colors disabled:opacity-60">
            {digesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} {t("os.admin.digest")}
          </button>
        </div>

        {/* Solicitudes de alta de empresas */}
        <div>
          <h3 className="text-lg font-medium mb-3">Solicitudes de empresas {pendingReqs.length > 0 && <span className="text-gold">· {pendingReqs.length}</span>}</h3>
          <div className="space-y-4" data-testid="admin-company-requests">
            {pendingReqs.length === 0 ? (
              <div className="bg-white border border-navy/10 rounded-lg text-navy/40 text-sm py-8 text-center" data-testid="admin-no-requests">No hay solicitudes pendientes.</div>
            ) : pendingReqs.map((r) => (
              <div key={r.id} data-testid={`admin-request-${r.id}`} className="bg-white border border-navy/10 rounded-lg p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-medium text-navy">{r.company_name}</div>
                    <div className="text-xs text-navy/40 mt-0.5">RFC: {r.rfc} · Admin: {r.admin_name} ({r.admin_email})</div>
                    <div className="text-xs text-navy/50 mt-1">Responsable legal: <b>{r.legal_rep_name}</b> · {r.legal_rep_contact}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`admin-approve-${r.id}`} onClick={() => approveReq(r)}
                      className="inline-flex items-center gap-1.5 text-sm rounded-full bg-emerald-500 text-white px-4 py-2 hover:opacity-90 transition-opacity">
                      <CheckCircle2 className="w-4 h-4" /> Aprobar
                    </button>
                    <button data-testid={`admin-reject-${r.id}`} onClick={() => rejectReq(r)}
                      className="inline-flex items-center gap-1.5 text-sm rounded-full border border-destructive/30 text-destructive px-4 py-2 hover:bg-destructive/5 transition-colors">
                      <X className="w-4 h-4" /> Rechazar
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {Object.entries(r.documents || {}).map(([k, doc]) => doc ? (
                    <a key={k} href={doc.data_url} download={doc.filename} data-testid={`admin-doc-${r.id}-${k}`}
                      className="inline-flex items-center gap-1.5 text-xs rounded-full bg-navy/[0.04] px-3 py-1.5 text-navy/60 hover:text-violet transition-colors">
                      <FileDown className="w-3.5 h-3.5" /> {k.replace(/_/g, " ")}
                    </a>
                  ) : (
                    <span key={k} className="inline-flex items-center gap-1.5 text-xs rounded-full bg-cream px-3 py-1.5 text-navy/30">{k.replace(/_/g, " ")}: sin archivo</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tenants / Empresas */}
        <div>
          <h3 className="text-lg font-medium mb-3">{t("os.admin.tenantsTitle")} <span className="text-navy/40">· {tenants.length}</span></h3>
          <div className="bg-white border border-navy/10 rounded-lg overflow-hidden" data-testid="admin-tenants-panel">
            {tenants.map((tn) => (
              <div key={tn.id} data-testid={`admin-tenant-row-${tn.id}`} className="grid grid-cols-[1.5fr_1fr_90px_90px_120px] gap-3 px-5 py-3 items-center border-b border-navy/5 last:border-0">
                <div className="font-medium text-sm flex items-center gap-2">
                  {tn.branding?.logo_url
                    ? <span className="w-6 h-6 rounded bg-navy flex items-center justify-center overflow-hidden shrink-0"><img src={mediaUrl(tn.branding.logo_url)} alt="" className="max-h-5 max-w-5 object-contain" /></span>
                    : tn.branding?.primary
                      ? <span className="w-3 h-3 rounded-full shrink-0" style={{ background: tn.branding.primary }} />
                      : null}
                  <span className="truncate">{tn.name}</span>
                </div>
                <div className="text-xs text-navy/40">{tn.slug}</div>
                <div className="text-xs text-navy/50">{tn.user_count} {t("os.admin.usersWord")}</div>
                <span className={`text-[11px] px-2 py-1 rounded-full justify-self-start ${STATUS_CLS[tn.status] || "bg-navy/5 text-navy/50"}`}>{tn.status}</span>
                <div className="flex items-center gap-1 justify-self-end">
                  <button data-testid={`admin-fiscal-tenant-${tn.id}`} onClick={() => setFiscalDialog({ open: true, tenant: tn })} title="Empresas fiscales (Finanzas)"
                    className="p-2 rounded-lg text-navy/30 hover:text-violet hover:bg-violet/5 transition-colors">
                    <Building2 className="w-4 h-4" />
                  </button>
                  <button data-testid={`admin-brand-tenant-${tn.id}`} onClick={() => setBrandDialog({ open: true, tenant: tn })} title="Personalizar marca (logo y colores)"
                    className="p-2 rounded-lg text-navy/30 hover:text-violet hover:bg-violet/5 transition-colors">
                    <Palette className="w-4 h-4" />
                  </button>
                  {tn.id !== "omnia-default" ? (
                    <button data-testid={`admin-delete-tenant-${tn.id}`} onClick={() => deleteTenant(tn)} title="Eliminar empresa"
                      className="p-2 rounded-lg text-navy/30 hover:text-destructive hover:bg-destructive/5 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  ) : <span className="w-8" />}
                </div>
              </div>
            ))}
            <div className="flex items-center gap-2 px-5 py-3 bg-cream/40">
              <button data-testid="admin-open-new-tenant" onClick={() => setBrandDialog({ open: true, tenant: null })}
                className="inline-flex items-center gap-1.5 text-sm rounded-full bg-gradient-violet text-white px-4 py-2 hover:opacity-90 transition-opacity">
                <Plus className="w-4 h-4" /> {t("os.admin.createCompany")}
              </button>
              <span className="text-xs text-navy/40">Con logo y colores personalizados</span>
            </div>
          </div>
        </div>

        {/* Pending */}
        <div>
          <h3 className="text-lg font-medium mb-3">{t("os.admin.pendingTitle")} {pending.length > 0 && <span className="text-gold">· {pending.length}</span>}</h3>
          <div className="bg-white border border-navy/10 rounded-lg overflow-x-auto" data-testid="admin-pending-panel">
            {pending.length === 0 ? <div className="text-navy/40 text-sm py-8 text-center">{t("os.admin.noPending")}</div>
              : pending.map((u) => <Row key={u.id} u={u} />)}
          </div>
        </div>

        {/* All users */}
        <div>
          <h3 className="text-lg font-medium mb-3">{t("os.admin.allTitle")}</h3>
          <div className="bg-white border border-navy/10 rounded-lg overflow-x-auto" data-testid="admin-users-panel">
            {rest.map((u) => <Row key={u.id} u={u} />)}
          </div>
        </div>
      </div>

      <TenantBrandingDialog
        open={brandDialog.open}
        tenant={brandDialog.tenant}
        onClose={() => setBrandDialog({ open: false, tenant: null })}
        onSaved={() => { loadTenants(); reloadBranding(); }}
      />

      <FiscalCompaniesDialog
        open={fiscalDialog.open}
        tenant={fiscalDialog.tenant}
        onClose={() => setFiscalDialog({ open: false, tenant: null })}
      />

      {compDialog.open && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-companies-dialog">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-editorial text-2xl">Empresas del usuario</h3>
              <button onClick={() => setCompDialog({ open: false, user: null, selected: [], roles: {}, default: null, loading: false, saving: false })} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-navy/55 mb-4">{compDialog.user?.name || compDialog.user?.email} · marca todas las empresas a las que pertenece. Podrá cambiar entre ellas con su selector global. La marcada como <b>predeterminada</b> es la que verá al iniciar sesión.</p>
            {compDialog.loading ? (
              <div className="py-8 flex justify-center text-navy/40"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : (
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {tenants.map((tn) => {
                  const sel = compDialog.selected.includes(tn.id);
                  const isDef = compDialog.default === tn.id;
                  return (
                    <div key={tn.id} data-testid={`admin-company-opt-${tn.id}`} className={`rounded-lg border px-3 py-2 transition-colors ${sel ? "border-violet bg-violet/5" : "border-navy/10"}`}>
                      <div className="flex items-center gap-3">
                        <input type="checkbox" checked={sel} onChange={() => toggleCompany(tn.id)} data-testid={`admin-company-check-${tn.id}`} className="w-4 h-4 accent-violet" />
                        <span className="flex-1 text-sm text-navy truncate">{tn.name}</span>
                        {sel && (
                          <button onClick={() => setCompDialog((d) => ({ ...d, default: tn.id }))} data-testid={`admin-company-default-${tn.id}`}
                            className={`text-[11px] px-2 py-1 rounded-full ${isDef ? "bg-gold/20 text-gold" : "text-navy/40 hover:text-violet border border-navy/10"}`}>
                            {isDef ? "Predeterminada" : "Hacer predeterminada"}
                          </button>
                        )}
                      </div>
                      {sel && (
                        <div className="flex items-center gap-2 mt-2 pl-7">
                          <span className="text-[11px] text-navy/45">Rol:</span>
                          <select value={compDialog.roles[tn.id] || compDialog.user?.role || "cliente"} onChange={(e) => setCompanyRole(tn.id, e.target.value)}
                            data-testid={`admin-company-role-${tn.id}`} className="text-[11px] bg-white border border-navy/10 rounded-lg px-2 py-1 outline-none focus:border-violet">
                            {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setCompDialog({ open: false, user: null, selected: [], roles: {}, default: null, loading: false, saving: false })} className="btn btn-ghost">Cancelar</button>
              <button onClick={saveCompanies} disabled={compDialog.saving || compDialog.loading} className="btn btn-primary gap-2" data-testid="admin-companies-save">
                {compDialog.saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </OSLayout>
  );
}
